// src/sections/admin/TenantsSection.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ResourceTable from '../../components/admin/ResourceTable';
import TenantForm from '../../components/admin/TenantForm';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

const TenantsSection = ({ user, tenants = [], setMsg, fetchAll }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingTenant, setEditingTenant] = useState(null);
  const [tenantStats, setTenantStats] = useState({});

  // Fetch tenant statistics
  const fetchTenantStats = async () => {
    if (user?.role !== 'vendor_admin' || tenants.length === 0) return;
    
    const stats = {};
    for (const tenant of tenants) {
      try {
        const response = await axios.get(`${BASE_URL}/api/tenants/${tenant.id}/stats`, {
          headers: {
            'X-User-Id': user.id,
            'X-User-Role': user.role,
            'X-Tenant-Id': user.tenant_id
          }
        });
        stats[tenant.id] = response.data;
      } catch (error) {
        console.error(`Error fetching stats for tenant ${tenant.id}:`, error);
        stats[tenant.id] = { user_count: 0, has_license: false, license_activated: false };
      }
    }
    setTenantStats(stats);
  };

  useEffect(() => {
    fetchTenantStats();
  }, [tenants, user?.role, user?.id, user?.tenant_id]);

  // Only vendor_admin should see this section
  if (user?.role !== 'vendor_admin') {
    return (
      <div>
        <h2 className="admin-section-header">Tenant Management</h2>
        <p>Access denied. Only vendor administrators can manage tenants.</p>
        <p>Debug: User role is "{user?.role}"</p>
      </div>
    );
  }

  const tenantColumns = [
    { key: 'id', label: 'ID' },
    { key: 'name', label: 'Tenant Name' },
    { 
      key: 'id', 
      label: 'Users', 
      render: (tenantId) => tenantStats[tenantId]?.user_count || 'Loading...'
    },
    { 
      key: 'id', 
      label: 'License', 
      render: (tenantId) => {
        const stats = tenantStats[tenantId];
        if (!stats) return 'Loading...';
        return stats.has_license ? (stats.license_activated ? 'Active' : 'Inactive') : 'None';
      }
    },
    { 
      key: 'id', 
      label: 'License Type', 
      render: (tenantId) => {
        const stats = tenantStats[tenantId];
        if (!stats || !stats.has_license) return 'N/A';
        return stats.license_type || 'Unknown';
      }
    },
    { 
      key: 'created_at', 
      label: 'Created', 
      render: (dateString) => dateString ? new Date(dateString).toLocaleDateString() : 'N/A'
    }
  ];



  const handleAddTenant = () => {
    setEditingTenant(null);
    setShowForm(true);
  };

  const handleEditTenant = (tenantData) => {
    setEditingTenant(tenantData);
    setShowForm(true);
  };

  const handleDeleteTenant = async (tenantData) => {
    if (window.confirm(`Are you sure you want to delete tenant "${tenantData.name}"? This will delete all associated data including users, counters, services, and licenses.`)) {
      try {
        await axios.delete(`${BASE_URL}/api/tenants/${tenantData.id}`, {
          headers: {
            'X-User-Id': user.id,
            'X-User-Role': user.role,
            'X-Tenant-Id': user.tenant_id
          }
        });
        setMsg({ type: 'success', text: `Tenant "${tenantData.name}" deleted successfully!` });
        fetchAll();
      } catch (error) {
        const errorMessage = error.response?.data?.error || error.message;
        setMsg({ type: 'error', text: `Failed to delete tenant: ${errorMessage}` });
        console.error("Error deleting tenant:", error);
      }
    }
  };

  const handleSubmitTenantForm = async (formData, isEditMode) => {
    try {
      const headers = {
        'X-User-Id': user.id,
        'X-User-Role': user.role,
        'X-Tenant-Id': user.tenant_id
      };

      if (isEditMode) {
        await axios.patch(`${BASE_URL}/api/tenants/${formData.id}`, formData, { headers });
        setMsg({ type: 'success', text: `Tenant "${formData.name}" updated successfully!` });
      } else {
        await axios.post(`${BASE_URL}/api/tenants`, formData, { headers });
        setMsg({ type: 'success', text: `Tenant "${formData.name}" created successfully!` });
      }
      setShowForm(false);
      setEditingTenant(null);
      fetchAll();
    } catch (error) {
      const errorMessage = error.response?.data?.error || error.message;
      setMsg({ type: 'error', text: `Failed to ${isEditMode ? 'update' : 'create'} tenant: ${errorMessage}` });
      console.error("Error submitting tenant form:", error);
    }
  };

  return (
    <div>
      <h2 className="admin-section-header">Tenant Management</h2>
      {!showForm ? (
        <>
          <button onClick={handleAddTenant} className="admin-button primary" style={{ marginBottom: '1.5rem' }}>
            Add New Tenant
          </button>
          <ResourceTable 
            data={tenants} 
            columns={tenantColumns} 
            onEdit={handleEditTenant} 
            onDelete={handleDeleteTenant} 
            title="Tenants"
          />
        </>
      ) : (
        <TenantForm 
          user={user} 
          initialData={editingTenant || {}} 
          onSubmit={handleSubmitTenantForm} 
          onCancel={() => setShowForm(false)} 
        />
      )}
    </div>
  );
};

export default TenantsSection; 